import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MagnifyingGlass, Crosshair, MapTrifold, UsersFour, Barbell, ArrowSquareOut, MapPin } from "@phosphor-icons/react";
import api from "../../lib/api";
import { FavouriteStar } from "../../components/FavouriteStar";
import { RatingBadge } from "../../components/ClassReview";

const pin = (color, active) => L.divIcon({
  className: "",
  html: `<div style="width:${active ? 22 : 16}px;height:${active ? 22 : 16}px;background:${color};border:2px solid #0A0A0A;box-shadow:0 0 0 2px ${color}66;transform:rotate(45deg)"></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lng], target.zoom || 12, { duration: 0.8 }); }, [target, map]);
  return null;
}

export default function ClientDiscover() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [fly, setFly] = useState(null);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = { q: q || undefined, type: type || undefined, lat: me?.lat, lng: me?.lng };
      api.get("/public/businesses", { params }).then(({ data }) => setData(data)).catch(() => setData({ businesses: [], total: 0 }));
    }, 250);
    return () => clearTimeout(t);
  }, [q, type, me]);

  const locate = () => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { const p = { lat: pos.coords.latitude, lng: pos.coords.longitude }; setMe(p); setFly({ ...p, zoom: 11 }); setLocating(false); },
      () => setLocating(false), { timeout: 8000 },
    );
  };

  const pinned = useMemo(() => (data?.businesses || []).filter((b) => b.lat != null && b.lng != null), [data]);
  const center = fly || (pinned[0] ? { lat: pinned[0].lat, lng: pinned[0].lng } : { lat: 54.3, lng: -2.5 });
  const focus = (b) => { setActive(b.id); if (b.lat != null) setFly({ lat: b.lat, lng: b.lng, zoom: 13 }); };

  return (
    <div className="space-y-6" data-testid="client-discover">
      <div>
        <div className="tracker text-brand mb-3">[ Discover ]</div>
        <h1 className="font-display text-4xl sm:text-5xl">Find a business near you.</h1>
        <p className="text-zinc-500 mt-2">Studios, coaches and class providers running on Traina — search by name or area, or drop a pin where you are.</p>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by business, coach or town…" className="w-full pl-9 pr-4 py-3 border text-sm" data-testid="discover-search" />
        </div>
        {[["", "All"], ["group", "Classes"], ["pt", "1:1 coaching"]].map(([v, l]) => (
          <button key={v} onClick={() => setType(v)} className={`tracker text-[10px] px-3 py-3 border ${type === v ? "border-brand text-white" : "border-white/10 text-zinc-500"}`} data-testid={`discover-type-${v || "all"}`}>{l}</button>
        ))}
        <button onClick={locate} disabled={locating} className="btn-ghost !py-3 !px-3 !text-[10px]" data-testid="discover-near-me"><Crosshair size={14} /> {locating ? "Locating…" : me ? "Near me ✓" : "Near me"}</button>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6">
        <div className="max-h-[560px] overflow-y-auto divide-y divide-white/5 border border-white/10" data-testid="discover-list">
          {!data ? <p className="p-8 text-center text-zinc-500 tracker">Searching…</p>
            : data.businesses.length === 0 ? (
              <div className="p-10 text-center" data-testid="discover-empty">
                <MapTrifold size={28} weight="duotone" className="text-brand mx-auto mb-3" />
                <p className="font-display text-xl">No businesses found.</p>
                <p className="text-zinc-500 text-sm mt-1">Try a different name or town.</p>
              </div>
            ) : data.businesses.map((b) => (
              <button key={b.id} onClick={() => focus(b)} className={`w-full text-left px-4 py-3 hover:bg-white/[0.03] ${active === b.id ? "bg-white/[0.05]" : ""}`} data-testid={`discover-biz-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rotate-45 shrink-0" style={{ background: b.accent }} />
                      <span className="font-display text-lg truncate">{b.brand_name}</span>
                      <FavouriteStar trainerId={b.id} brandName={b.brand_name} size={14} accent={b.accent} />
                      <RatingBadge rating={b.rating} testId={`discover-rating-${b.id}`} />
                      <span className="tracker text-[9px] px-1.5 py-0.5 border border-white/10 text-zinc-400 inline-flex items-center gap-1">
                        {b.business_type === "group" ? <UsersFour size={10} /> : <Barbell size={10} />}{b.business_type === "group" ? "CLASSES" : "1:1"}
                      </span>
                    </div>
                    {b.tagline && <div className="text-xs text-zinc-400 mt-0.5 truncate">{b.tagline}</div>}
                    <div className="tracker text-[9px] text-zinc-600 mt-1 inline-flex items-center gap-1"><MapPin size={10} /> {b.city || b.address || "Location not listed"}{b.distance_km != null ? ` · ${b.distance_km} km away` : ""}</div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <a href={b.landing_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="btn-ghost !py-1 !px-2 !text-[9px]" data-testid={`discover-visit-${b.id}`}>Visit <ArrowSquareOut size={10} /></a>
                    {b.group_url && <a href={b.group_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="btn-primary !py-1 !px-2 !text-[9px]" data-testid={`discover-classes-${b.id}`}>Classes</a>}
                  </div>
                </div>
              </button>
            ))}
        </div>

        <div className="h-[560px] border border-white/10 overflow-hidden discover-map" data-testid="discover-map">
          <MapContainer center={[center.lat, center.lng]} zoom={fly?.zoom || 6} scrollWheelZoom className="h-full w-full">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FlyTo target={fly} />
            {me && <Marker position={[me.lat, me.lng]} icon={pin("#ffffff", false)}><Popup>You are here</Popup></Marker>}
            {pinned.map((b) => (
              <Marker key={b.id} position={[b.lat, b.lng]} icon={pin(b.accent, active === b.id)} eventHandlers={{ click: () => setActive(b.id) }}>
                <Popup>
                  <div style={{ minWidth: 160 }}>
                    <strong>{b.brand_name}</strong><br />
                    <span style={{ fontSize: 11 }}>{b.address}</span><br />
                    <a href={b.landing_url} target="_blank" rel="noopener noreferrer">Visit page</a>
                    {b.group_url && <> · <a href={b.group_url} target="_blank" rel="noopener noreferrer">Book a class</a></>}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    </div>
  );
}
